/**
 * Ashrafie Referral Attribution — capture + attribute
 *
 * Last-mile FE wiring for the Ashrafie referral programme. A referral invite
 * link looks like `${appBaseUrl}/{locale}/register?ref=CODE`. When a prospective
 * user lands via that link we:
 *
 *   1. Capture the `?ref=` code once, early, and persist it to sessionStorage so
 *      it survives the auth overlay flow, page navigations and redirects.
 *   2. Attribute it to the user ONLY after a genuinely new account is created
 *      (register — never login), fire-and-forget. A referral failure must never
 *      block or error the auth UX, so every path here swallows errors. The
 *      backend independently no-ops invalid / duplicate / self-referral codes.
 *
 * @module lib/api/ashrafie/referralAttribution
 */

import { useCallback, useEffect } from 'react';

import { useAttributeReferral } from './hooks';

/** Storage key holding the pending referral code (both session + durable layers). */
export const REFERRAL_STORAGE_KEY = 'ashrafie_ref';

/**
 * Durable (localStorage) time-to-live. A referral must outlive a tab close and a
 * cross-tab landing, but not linger indefinitely, so we cap it at ~30 days —
 * matching a typical last-touch attribution window.
 */
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Accepted referral-code shape. Backend codes are alphanumeric VarChar(16);
 * we allow hyphens and cap at 32 chars defensively so a malformed / hostile
 * query param can never reach the API.
 */
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9-]{1,32}$/;

/**
 * Validate and normalise a raw referral code. Returns the trimmed code when it
 * matches the accepted format, otherwise `null`.
 */
export function sanitizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  return REFERRAL_CODE_PATTERN.test(trimmed) ? trimmed : null;
}

/**
 * sessionStorage access can throw (Safari private mode, disabled storage, SSR).
 * Resolve it defensively so callers never have to guard.
 */
function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** localStorage access can throw for the same reasons — resolve it defensively. */
function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Persist to the durable (localStorage) layer with a TTL envelope so the referral
 * survives a tab close / cross-tab landing but self-expires after ~30 days.
 * Best-effort — never throws.
 */
function persistDurableReferralCode(code: string): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(
      REFERRAL_STORAGE_KEY,
      JSON.stringify({ code, expiresAt: Date.now() + REFERRAL_TTL_MS })
    );
  } catch {
    // quota / privacy errors are non-fatal — the session layer still applies.
  }
}

/**
 * Read the durable referral code, honouring the TTL envelope. Expired or malformed
 * entries are cleared and treated as absent. Returns a re-sanitized code or `null`.
 */
function readDurableReferralCode(): string | null {
  const store = safeLocalStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown; expiresAt?: unknown };
    if (typeof parsed.expiresAt === 'number' && parsed.expiresAt < Date.now()) {
      store.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return sanitizeReferralCode(typeof parsed.code === 'string' ? parsed.code : null);
  } catch {
    return null;
  }
}

/**
 * Persist a (pre-sanitized) referral code across BOTH layers: sessionStorage for
 * the in-flight auth flow and a durable localStorage copy (with TTL) so a referral
 * survives tab close / cross-tab landing. Best-effort — never throws.
 */
export function persistReferralCode(code: string): void {
  const session = safeSessionStorage();
  if (session) {
    try {
      session.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {
      // quota / privacy errors are non-fatal — attribution is best-effort.
    }
  }
  persistDurableReferralCode(code);
}

/**
 * Read the pending referral code, re-sanitized. Reads through the session layer
 * first (same-tab, freshest), then falls back to the durable layer so a code
 * captured in another tab or before a tab close is still recovered. Returns
 * `null` when absent/invalid.
 */
export function readStoredReferralCode(): string | null {
  const session = safeSessionStorage();
  if (session) {
    try {
      const fromSession = sanitizeReferralCode(session.getItem(REFERRAL_STORAGE_KEY));
      if (fromSession) return fromSession;
    } catch {
      // fall through to the durable layer.
    }
  }
  return readDurableReferralCode();
}

/** Remove the pending referral code from BOTH layers. Best-effort — never throws. */
export function clearStoredReferralCode(): void {
  const session = safeSessionStorage();
  if (session) {
    try {
      session.removeItem(REFERRAL_STORAGE_KEY);
    } catch {
      // ignore — nothing actionable.
    }
  }
  const local = safeLocalStorage();
  if (local) {
    try {
      local.removeItem(REFERRAL_STORAGE_KEY);
    } catch {
      // ignore — nothing actionable.
    }
  }
}

/**
 * Parse a `?ref=` code out of a URL query string and persist it. First-touch
 * wins because capture runs once on load. Never throws.
 */
export function captureReferralCodeFromSearch(search: string): void {
  try {
    const code = sanitizeReferralCode(new URLSearchParams(search).get('ref'));
    if (code) persistReferralCode(code);
  } catch {
    // malformed query string — nothing to capture.
  }
}

/**
 * Capture a referral code from the current URL exactly once on mount and persist
 * it for later attribution. Mount high in the tree (app root) so a referral
 * landing is captured before the auth overlay or any redirect can strip the query.
 */
export function useCaptureReferralOnLoad(): void {
  useEffect(() => {
    captureReferralCodeFromSearch(window.location.search);
  }, []);
}

/**
 * Module-scoped guard against a double-submit. The stored code is deliberately NOT
 * cleared up-front (a 401 token-race right after signup used to drop it permanently);
 * instead this flag prevents a concurrent re-fire while a request is in flight, and
 * the code is only cleared once attribution genuinely succeeds.
 */
let attributionInFlight = false;

/**
 * Returns a stable fire-and-forget callback that attributes any captured referral
 * code to the just-registered user. Call ONLY on a genuinely new account
 * (register — not login).
 *
 * The stored code is cleared ONLY on the mutation's success, so a failed/401
 * attribution keeps the code for a later retry instead of silently dropping it.
 * An in-flight flag (not an early clear) guards against a double-submit. All errors
 * are swallowed — this must never affect the auth UX.
 */
export function useAttributeStoredReferral(): () => void {
  const { mutate } = useAttributeReferral();

  return useCallback<() => void>(() => {
    const code = readStoredReferralCode();
    if (!code) return;
    if (attributionInFlight) return; // already attributing — never double-fire.

    attributionInFlight = true;
    try {
      mutate(
        { code },
        {
          onSuccess: () => {
            // Only now is it safe to drop the code — attribution landed.
            clearStoredReferralCode();
            attributionInFlight = false;
          },
          onError: () => {
            // Keep the code for a future retry; just release the in-flight lock.
            attributionInFlight = false;
          },
        }
      );
    } catch {
      // `mutate` should never throw synchronously; stay defensive regardless.
      attributionInFlight = false;
    }
  }, [mutate]);
}
